import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';
import api from '../api/axios';
import { mergeComponentType } from '../utils/componentValues';
import { trackAddToCart } from '../utils/analytics';
import { getItemSelections, matchesCartItem, normalizeSelections } from '../utils/productLinks';
import { resolveSelection } from '../utils/productSelection';

const CartContext = createContext({});

export const useCart = () => useContext(CartContext);

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api';
const CART_STORAGE_KEY = 'hideawayhome_guest_cart';

const cartSignature = items => JSON.stringify((items || []).map(item => {
  const selections = item.selected_components || Object.fromEntries(
    (item.component_selections || []).map(selection => [selection.component_type_id, selection.value_id])
  );
  return JSON.stringify([item.product_id, item.variant_id || '', item.quantity,
    Object.entries(selections).sort(([left], [right]) => left.localeCompare(right))]);
}).sort());

async function getMergedComponentType(product, componentTypeId) {
  const overrides = product.custom_component_values?.[componentTypeId];
  try {
    const response = await axios.get(`${API_BASE_URL}/products/component-types/${componentTypeId}`);
    return mergeComponentType(response.data, overrides);
  } catch {
    const comp = product.components?.find((c) => c.component_type_id === componentTypeId);
    if (comp && overrides?.length) {
      return {
        id: componentTypeId,
        name: comp.component_type_name,
        description: null,
        values: overrides
      };
    }
    return null;
  }
}

/**
 * Cart Context with Persistence Rules:
 * 
 * 1. Guest (not logged in):
 *    - Cart stored in localStorage only
 *    - Survives page refresh
 *    - Lost when localStorage is cleared
 * 
 * 2. Logged in:
 *    - Cart stored in Firestore under carts/{uid}
 *    - Persists across devices
 *    - Syncs in real-time
 * 
 * 3. On login:
 *    - Merge localStorage cart into Firestore cart
 *    - Conflict resolution: Add quantities for same products
 *    - Clear localStorage after merge
 */
export const CartProvider = ({ children }) => {
  const { currentUser, loading: authLoading } = useAuth();
  const [cart, setCartState] = useState({ items: [] });
  const cartRef = useRef(cart);
  const mutationQueueRef = useRef(Promise.resolve());
  const initializationRef = useRef(null);
  const activeUserIdRef = useRef(currentUser?.uid || null);
  activeUserIdRef.current = currentUser?.uid || null;
  const [loading, setLoading] = useState(true);
  const [initializedUserId, setInitializedUserId] = useState(undefined);
  const [syncing, setSyncing] = useState(false);
  const [isMiniCartOpen, setIsMiniCartOpen] = useState(false);

  const openMiniCart = () => setIsMiniCartOpen(true);
  const closeMiniCart = () => setIsMiniCartOpen(false);

  const setCart = updatedCart => {
    if (activeUserIdRef.current !== (currentUser?.uid || null)) return;
    cartRef.current = updatedCart;
    setCartState(updatedCart);
  };

  // Cart writes replace the complete document. Serialize them so rapid adds
  // and price refreshes cannot overwrite a newer change with a stale snapshot.
  const queueMutation = mutation => {
    const userId = currentUser?.uid || null;
    const result = mutationQueueRef.current.then(async () => {
      await initializationRef.current?.promise;
      if (activeUserIdRef.current !== userId) throw new Error('Your account changed. Please try again.');
      return mutation();
    });
    mutationQueueRef.current = result.catch(() => {});
    return result;
  };

  // Initialize cart on mount and when auth state changes
  useEffect(() => {
    if (!authLoading) initializeCart();
  }, [currentUser, authLoading]);

  /**
   * Initialize cart based on authentication state
   */
  const initializeCart = () => {
    const userId = currentUser?.uid || null;
    if (initializationRef.current?.userId === userId) return initializationRef.current.promise;
    const promise = (async () => {
      setLoading(true);
      try {
        if (currentUser) {
          await loadFirestoreCart();
          if (activeUserIdRef.current !== userId) return;
          const guestCart = loadLocalStorageCart();
          if (guestCart.items.length > 0) await mergeGuestCartOnLogin(guestCart);
        } else {
          setCart(loadLocalStorageCart());
        }
      } finally {
        if (activeUserIdRef.current === userId) {
          setInitializedUserId(userId);
          setLoading(false);
        }
      }
    })();
    initializationRef.current = { userId, promise };
    promise.finally(() => {
      if (initializationRef.current?.promise === promise) initializationRef.current = null;
    });
    return promise;
  };

  /**
   * Load cart from localStorage (guest cart)
   */
  const loadLocalStorageCart = () => {
    try {
      const stored = localStorage.getItem(CART_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          items: Array.isArray(parsed?.items) ? parsed.items : [],
          updated_at: parsed.updated_at || new Date().toISOString()
        };
      }
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
    }
    return { items: [], updated_at: new Date().toISOString() };
  };

  /**
   * Save cart to localStorage (guest cart)
   */
  const saveLocalStorageCart = (cartData) => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify({
        items: cartData.items,
        updated_at: new Date().toISOString()
      }));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  };

  /**
   * Clear localStorage cart
   */
  const clearLocalStorageCart = () => {
    try {
      localStorage.removeItem(CART_STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing localStorage cart:', error);
    }
  };

  /**
   * Load cart from backend (logged-in user)
   */
  const loadFirestoreCart = async (preserveOnError = false) => {
    if (!currentUser) return;
    
    try {
      const response = await api.get('/cart');
      setCart({
        items: response.data.items || [],
        updated_at: response.data.updated_at
      });
    } catch (error) {
      console.error('Error loading cart from backend:', error);
      if (preserveOnError) throw error;
      // If cart doesn't exist or error, start with empty cart
      setCart({ items: [] });
    }
  };

  /**
   * Save cart to backend (logged-in user)
   */
  const saveFirestoreCart = async (cartData) => {
    if (!currentUser) return;
    if (activeUserIdRef.current !== currentUser.uid) throw new Error('Your account changed. Please try again.');
    
    try {
      await api.post('/cart', {
        items: cartData.items
      });
    } catch (error) {
      console.error('Error saving cart to backend:', error);
      throw error;
    }
  };

  /**
   * Merge guest cart into backend cart on login
   * Conflict resolution: Add quantities for same products
   */
  const mergeGuestCartOnLogin = async (guestCart) => {
    if (!currentUser || guestCart.items.length === 0) return;
    
    setSyncing(true);
    
    try {
      // Send guest cart to backend for merging
      const response = await api.post('/cart/merge', {
        guest_items: guestCart.items
      });
      
      // Update local state with merged cart
      setCart({ items: response.data.items || [] });
      
      // Clear guest cart from localStorage
      clearLocalStorageCart();
      
      console.log('Guest cart merged successfully');
    } catch (error) {
      console.error('Error merging guest cart:', error);
    } finally {
      setSyncing(false);
    }
  };

  /**
   * Fetch product details for cart items
   */
  const fetchProductDetails = async (productId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/products/${productId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching product:', error);
      return null;
    }
  };

  /**
   * Add item to cart
   */
  const addToCart = async (productId, quantity = 1, variantId = null, selectedComponents = {}) => {
    try {
      selectedComponents = normalizeSelections(selectedComponents);
      variantId = variantId || null;
      if (!Number.isInteger(quantity) || quantity < 1) {
        throw new Error('Quantity must be a positive whole number');
      }
      // Fetch product details to get current price and name
      const product = await fetchProductDetails(productId);
      if (!product) {
        throw new Error('Product not found');
      }

      // Recheck the configuration against the current catalog before saving it.
      // Catalog edits or a failed option lookup must not silently add a base item.
      if (product.active === false) throw new Error('This product is no longer available.');
      const componentTypes = Object.fromEntries(await Promise.all(
        (product.components || [])
          .filter(component => selectedComponents[component.component_type_id])
          .map(async component => [component.component_type_id,
            await getMergedComponentType(product, component.component_type_id)])
      ));
      const selection = resolveSelection(product, componentTypes, { variantId, components: selectedComponents, quantity });
      if (selection.invalidVariant || selection.variantMissing) {
        throw new Error('Please choose an available product option.');
      }
      if (selection.requiredMissing.length || selection.invalidSelections.length) {
        throw new Error('We could not verify your selected options. Please reopen the product and choose them again.');
      }
      const price = selection.unitPrice;
      const variantName = selection.variant?.name || null;
      const componentSelections = selection.componentSelections;
      const available = selection.stock;

      const newItem = {
        product_id: productId,
        product_name: product.name,
        quantity: quantity,
        price: price,
        variant_id: variantId,
        variant_name: variantName,
        selected_components: selectedComponents,
        component_selections: componentSelections,
        added_at: new Date().toISOString()
      };

      const currentCart = cartRef.current;
      const updatedCart = { ...currentCart, items: currentCart.items.map(item => ({ ...item })) };

      // Check if item already exists
      const existingIndex = updatedCart.items.findIndex(
        item => matchesCartItem(item, productId, variantId, selectedComponents)
      );

      // Enforce stock against the resulting quantity (existing in cart + this add)
      const resultingQty = (existingIndex >= 0 ? updatedCart.items[existingIndex].quantity : 0) + quantity;
      if (available != null && resultingQty > available) {
        throw new Error(
          available <= 0
            ? 'This option is out of stock'
            : `Only ${available} in stock${existingIndex >= 0 ? ' (already in your cart)' : ''}`
        );
      }

      if (existingIndex >= 0) {
        // Keep the displayed name, options and current price in sync with the
        // configuration the shopper just reviewed, including older saved carts.
        updatedCart.items[existingIndex] = {
          ...updatedCart.items[existingIndex], ...newItem, quantity: resultingQty,
        };
      } else {
        // Add new item
        updatedCart.items.push(newItem);
      }

      // Save cart based on auth state
      if (currentUser) {
        await saveFirestoreCart(updatedCart);
      } else {
        saveLocalStorageCart(updatedCart);
      }

      setCart(updatedCart);

      // GA4 add_to_cart fires only after a successful save so failed adds
      // don't pollute conversion analytics.
      trackAddToCart({ ...product, price }, quantity, variantName);

      return updatedCart;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  };

  /**
   * Update cart item quantity
   */
  const updateCartItem = async (productId, quantity, variantId = null, selectedComponents = {}) => {
    try {
      if (!Number.isInteger(quantity)) {
        throw new Error('Quantity must be a whole number');
      }
      if (quantity < 1) {
        return await removeFromCart(productId, variantId, selectedComponents);
      }

      const currentCart = cartRef.current;
      const updatedCart = { ...currentCart, items: currentCart.items.map(item => ({ ...item })) };
      const itemIndex = updatedCart.items.findIndex(
        item => matchesCartItem(item, productId, variantId, selectedComponents)
      );

      if (itemIndex >= 0) {
        updatedCart.items[itemIndex].quantity = quantity;

        // Save cart based on auth state
        if (currentUser) {
          await saveFirestoreCart(updatedCart);
        } else {
          saveLocalStorageCart(updatedCart);
        }

        setCart(updatedCart);
      }

      return updatedCart;
    } catch (error) {
      console.error('Error updating cart item:', error);
      throw error;
    }
  };

  /**
   * Remove item from cart
   */
  const removeFromCart = async (productId, variantId = null, selectedComponents = {}) => {
    try {
      const updatedCart = { ...cartRef.current };
      updatedCart.items = updatedCart.items.filter(
        item => !matchesCartItem(item, productId, variantId, selectedComponents)
      );

      // Save cart based on auth state
      if (currentUser) {
        await saveFirestoreCart(updatedCart);
      } else {
        saveLocalStorageCart(updatedCart);
      }

      setCart(updatedCart);
      return updatedCart;
    } catch (error) {
      console.error('Error removing from cart:', error);
      throw error;
    }
  };

  /**
   * Clear entire cart
   */
  const clearCart = async () => {
    try {
      const emptyCart = { items: [] };

      // Save cart based on auth state
      if (currentUser) {
        await saveFirestoreCart(emptyCart);
      } else {
        saveLocalStorageCart(emptyCart);
      }

      setCart(emptyCart);
    } catch (error) {
      console.error('Error clearing cart:', error);
      throw error;
    }
  };

  const completeCheckout = async order => {
    // A customer can add more items while a redirect payment is processing.
    // Only clear the guest cart when it still matches the purchased contents.
    if (order.is_guest) {
      const guestCart = loadLocalStorageCart();
      if (cartSignature(guestCart.items) === cartSignature(order.items)) {
        saveLocalStorageCart({ items: [] });
      }
    }
    if (currentUser) {
      // The server compares and clears the paid cart transactionally. Reload
      // its result instead of posting an empty cart over newer customer edits.
      await loadFirestoreCart(true);
    } else {
      setCart(loadLocalStorageCart());
    }
  };

  /**
   * Compute the effective unit price for a product (async: merges per-product component modifiers with catalog).
   */
  const computeEffectivePriceAsync = async (product, variantId, selectedComponents) => {
    const now = new Date();
    const saleStartOk = !product.sale_start_date || new Date(product.sale_start_date) <= now;
    const saleEndOk = !product.sale_end_date || new Date(product.sale_end_date) >= now;
    const saleActive = product.sale_price && product.sale_price > 0 && saleStartOk && saleEndOk;

    let price = product.price;

    if (variantId && product.variants) {
      const variant = product.variants.find(v => v.id === variantId);
      if (variant) {
        if (variant.price_override) price = variant.price_override;
        if (saleActive) {
          if (variant.sale_price_override && variant.sale_price_override > 0) {
            price = variant.sale_price_override;
          } else if (product.sale_price) {
            price = product.sale_price;
          }
        }
      }
    } else if (saleActive) {
      price = product.sale_price;
    }

    const selections = selectedComponents && typeof selectedComponents === 'object' ? selectedComponents : {};
    const cache = new Map();
    for (const [componentTypeId, valueId] of Object.entries(selections)) {
      let mergedType = cache.get(componentTypeId);
      if (!mergedType) {
        mergedType = await getMergedComponentType(product, componentTypeId);
        cache.set(componentTypeId, mergedType);
      }
      const value = mergedType?.values?.find(v => v.id === valueId);
      price += value?.price_modifier || 0;
    }
    return price;
  };

  /**
   * Refresh cart item prices to match current product sale state.
   * Call this when opening the cart page to ensure prices are up-to-date.
   */
  const refreshCartPrices = async () => {
    const currentCart = cartRef.current;
    if (!currentCart.items || currentCart.items.length === 0) return;

    try {
      const uniqueIds = [...new Set(currentCart.items.map(i => i.product_id))];
      const responses = await Promise.all(
        uniqueIds.map(id => axios.get(`${API_BASE_URL}/products/${id}`).catch(() => null))
      );
      const productsMap = {};
      responses.forEach(r => { if (r?.data) productsMap[r.data.id] = r.data; });

      let changed = false;
      const updatedItems = [];
      for (const item of currentCart.items) {
        const product = productsMap[item.product_id];
        if (!product) {
          updatedItems.push(item);
          continue;
        }
        const correctPrice = await computeEffectivePriceAsync(
          product,
          item.variant_id,
          getItemSelections(item)
        );
        if (Math.abs(item.price - correctPrice) > 0.001) {
          changed = true;
          updatedItems.push({ ...item, price: correctPrice });
        } else {
          updatedItems.push(item);
        }
      }

      if (changed) {
        const updatedCart = { ...currentCart, items: updatedItems };
        if (currentUser) {
          await saveFirestoreCart(updatedCart);
        } else {
          saveLocalStorageCart(updatedCart);
        }
        setCart(updatedCart);
      }
    } catch (error) {
      console.error('Error refreshing cart prices:', error);
    }
  };

  /**
   * Get cart total
   */
  const getCartTotal = () => {
    return cart.items.reduce((total, item) => {
      return total + (item.price * item.quantity);
    }, 0);
  };

  /**
   * Get cart item count
   */
  const getCartItemCount = () => {
    return cart.items.reduce((count, item) => count + item.quantity, 0);
  };

  /**
   * Get item from cart
   */
  const getCartItem = (productId, variantId = null, selectedComponents = {}) => {
    return cart.items.find(
      item => matchesCartItem(item, productId, variantId, selectedComponents)
    );
  };

  const value = {
    cart,
    loading: loading || authLoading || initializedUserId !== (currentUser?.uid || null),
    syncing,
    addToCart: (...args) => queueMutation(() => addToCart(...args)),
    updateCartItem: (...args) => queueMutation(() => updateCartItem(...args)),
    removeFromCart: (...args) => queueMutation(() => removeFromCart(...args)),
    clearCart: () => queueMutation(clearCart),
    completeCheckout: order => queueMutation(() => completeCheckout(order)),
    getCartTotal,
    getCartItemCount,
    getCartItem,
    refreshCart: initializeCart,
    refreshCartPrices: () => queueMutation(refreshCartPrices),
    isMiniCartOpen,
    openMiniCart,
    closeMiniCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
